import { Fragment, useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@app/components/ui/table";
import { Button } from "@app/components/ui/button";
import { Trash2, ChevronDown, ChevronUp, Eye, ExternalLink, ArrowUp, ArrowDown, Loader2, CalendarClock } from "lucide-react";
import { Link } from "react-router";
import { PatientDetailModal } from "@app/components/PatientDetailModal";
import { Plural, Trans, msg, useLingui } from "@lingui/macro";
import type { MessageDescriptor } from "@lingui/core";

/**
 * L'état de revue d'un dossier, dans les mêmes termes partout.
 *
 * Les quatre nombres sont ceux de ``/correction/stats`` — le geste GOUVERNANT de chaque
 * champ, élu comme ``patient_field_resolution.get_truth_feedback_at`` l'élit — et ce sont
 * exactement les trois pastilles de la fiche patient : ``validated`` la pastille verte,
 * ``corrected`` la croix rouge, ``flagged`` la pastille grise. Un dossier qui affiche
 * huit croix rouges compte donc huit corrigés ici, sans qu'aucun écran n'ait à recompter.
 */
export interface ExampleReview {
  reviewed: number;
  validated: number;
  corrected: number;
  flagged: number;
}

/** Clé de réponse VIVE : ce qu'une expérience noterait maintenant, résolue par le serveur avec la
 * même fonction que le scorer. À ne pas confondre avec `expected_outputs`, qui est l'instantané
 * figé à l'association du patient et qui a divergé sur tous les jeux de données en production. */
interface AnswerKey {
  values: Record<string, unknown>;
  /** Champs que le clinicien a explicitement VIDÉS. « Rien » est la bonne réponse pour eux, et
   * l'instantané ne sait pas les exprimer : une colonne NULL ne distingue pas un champ vidé d'un
   * champ jamais touché. */
  emptied: string[];
  size: number;
  /** `field → ISO day`: the CLINICAL day each answer describes.
   *
   *  Empty when the key was resolved without a date (undated dataset, no snapshot): the value then
   *  comes from the truth row rather than from a gesture, and pinning a day on it would be inventing
   *  a provenance. */
  validated_on?: Record<string, string>;
}

/** One key value, as it is read — never as raw JSON.
 *
 *  An array is rendered by its elements and not by its brackets: `["L4-L5","L5-S1"]` reads
 *  « L4-L5 · L5-S1 », as everywhere else in the application. */
function readValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.map((v) => readValue(v)).join(" · ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** The key under which a dated row names its act. */
const EPISODE_KEY = "episode";

/** What the technical keys mean, when they have a readable name. A key absent from here shows as
 *  itself: inventing a label for a metadata entry we do not know would be worse than showing its
 *  name.
 *
 *  `msg` descriptors rather than strings: this table renders in all three locales, and a constant
 *  defined outside the component cannot carry `<Trans>`. The label is resolved at render time, with
 *  the component's own `t`. */
const METADATA_LABELS: Record<string, MessageDescriptor> = {
  [EPISODE_KEY]: msg`Acte`,
  batch_eval: msg`Créée par l'évaluation`,
  patient_id_magex: msg`Dossier`,
  tenant_id: msg`Établissement`,
  id_patient: msg`N° patient`,
};

/** The act, as a sentence rather than an object: rank, indication, and the pathway generation it is
 *  read from — that last one because a rebuilt pathway moves the acts under the rows. */
function describeEpisode(value: unknown, t: (descriptor: MessageDescriptor) => string): string {
  const act = value as ExampleEpisode | null;
  if (!act || typeof act.rank !== "number") return readValue(value);
  const parts = [t(msg`Épisode ${act.rank}`)];
  if (act.label) parts.push(act.label);
  if (act.build_version) parts.push(act.build_version);
  return parts.join(" · ");
}

function shortDay(iso: string | undefined): string | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString("fr-FR");
}

/** The act a dated example describes, written by `services/episode_examples`.
 *
 *  Read from `metadata` rather than from a column: an example is identified by (patient, clinical
 *  day), and the episode is what makes that day intelligible — not a second identity for the row.
 *  Absent on every example predating this axis, and on every record whose pathway was never
 *  built. */
interface ExampleEpisode {
  id?: string;
  rank: number;
  concept?: string;
  label?: string | null;
  build_version?: string;
}

function episodeOf(example: DatasetExample): ExampleEpisode | null {
  const raw = example.metadata?.episode;
  if (!raw || typeof raw !== "object") return null;
  const act = raw as ExampleEpisode;
  return typeof act.rank === "number" ? act : null;
}

interface DatasetExample {
  id: string;
  inputs: Record<string, unknown>;
  expected_outputs: Record<string, unknown> | null;
  answer_key?: AnswerKey | null;
  metadata: Record<string, unknown> | null;
  source_type: string | null;
  /** Date clinique de la ligne (AAAA-MM-JJ) : l'exemple décrit le patient à ce jour-là.
   * null = ligne non datée, c'est-à-dire tous les documents / l'état courant. */
  as_of?: string | null;
  created_at: string;
  updated_at: string;
}

interface ExamplesTableProps {
  examples: DatasetExample[];
  onDelete?: (exampleId: string) => void;
  onEdit?: (example: DatasetExample) => void;
  /** Total d'exemples côté serveur (pour pagination) */
  total?: number;
  /** Callback pour charger la page suivante */
  onLoadMore?: () => void;
  /** Chargement de la page suivante en cours */
  loadingMore?: boolean;
  /**
   * Quand fourni, seuls les exemples dont le patient (metadata patient_id_magex) est dans cette map
   * auront un lien "Voir le patient" (ex. patients appartenant au médecin connecté).
   * Clé = id_magex, valeur = chemin vers la fiche patient.
   */
  allowedPatientLinks?: Map<string, string>;
  /**
   * Map exemple id → url fiche patient. Prioritaire sur allowedPatientLinks quand fourni
   * (résolution faite côté parent, avec fallback tenant_id + id_patient).
   */
  patientLinkByExampleId?: Map<string, string>;
  /** Example id → patient { nom, prenom }. Wins over the names written on the example metadata. */
  patientNameByExampleId?: Map<string, { nom: string | null; prenom: string | null }>;
  /** Afficher la colonne Patient même sans lien (ex. page dataset pour garder la structure). */
  alwaysShowPatientColumn?: boolean;
  /** Indique que les liens patient sont en cours de chargement (affiche un spinner plutôt qu'un tiret). */
  patientLinksLoading?: boolean;
  /**
   * Map exemple id → état de revue du dossier, ou `undefined` si aucun geste n'a été
   * posé dessus. C'est la seule colonne qui répond « ce dossier a-t-il été revu » à
   * l'endroit où on balaie la cohorte ; l'agrégat vit dans l'onglet Corrections.
   */
  reviewByExampleId?: Map<string, ExampleReview>;
  /**
   * The rows of a sealed reference (PT-155): which dossier, which act, which day — read-only. Nothing
   * that reads its answers is shown: no expected output, no review, no patient page, no action.
   */
  sealed?: boolean;
}

/** Patient names the backend wrote on the example metadata when the patient joined the dataset. */
function useMetadataNameMap(examples: DatasetExample[]) {
  return useMemo(() => {
    const nameMap = new Map<
      string,
      { nom: string | null; prenom: string | null }
    >();
    for (const ex of examples) {
      const metaNom = ex.metadata?.nom as string | null | undefined;
      const metaPrenom = ex.metadata?.prenom as string | null | undefined;
      if (metaNom != null || metaPrenom != null) {
        nameMap.set(ex.id, {
          nom: metaNom ?? null,
          prenom: metaPrenom ?? null,
        });
      }
    }
    return nameMap;
  }, [examples]);
}

export function ExamplesTable({
  examples,
  onDelete,
  onEdit,
  total,
  onLoadMore,
  loadingMore,
  allowedPatientLinks,
  patientLinkByExampleId,
  reviewByExampleId,
  patientNameByExampleId,
  alwaysShowPatientColumn: _alwaysShowPatientColumn,
  patientLinksLoading = false,
  sealed = false,
}: ExamplesTableProps) {
  const { t } = useLingui();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Le dossier ouvert par l'œil, ET le jour qu'il décrit. Les deux ensemble parce qu'une ligne de
  // jeu de données EST un patient à une date : l'ouvrir à aujourd'hui montrerait le bon dossier
  // au mauvais jour, et les valeurs ne seraient plus celles que cette ligne fait noter.
  const [modalTarget, setModalTarget] = useState<{
    path: string;
    at: string | null;
  } | null>(null);
  const [sortDir, setSortDir] = useState<"desc" | "asc">("desc");
  const metadataNames = useMetadataNameMap(examples);

  const patientNames = useMemo(() => {
    if (patientNameByExampleId == null) return metadataNames;
    const merged = new Map(metadataNames);
    for (const [k, v] of patientNameByExampleId) {
      merged.set(k, v);
    }
    return merged;
  }, [metadataNames, patientNameByExampleId]);

  const patientLinks = useMemo(() => {
    if (patientLinkByExampleId != null) return patientLinkByExampleId;
    if (allowedPatientLinks != null) {
      const map = new Map<string, string>();
      for (const ex of examples) {
        const idMagex = (ex.metadata?.patient_id_magex ??
          ex.inputs?.patient_id_magex) as string | undefined;
        if (idMagex) {
          const path = allowedPatientLinks.get(idMagex);
          if (path) map.set(ex.id, path);
        }
      }
      return map;
    }
    return new Map<string, string>();
  }, [allowedPatientLinks, examples, patientLinkByExampleId]);

  /** The rows, grouped by RECORD then chronological inside a record.
   *
   *  A revised record carries one row per act. Sorted by date alone they end up separated by other
   *  patients' rows, and nothing says they are the same person — the indication was what let you
   *  guess. Grouped, the table becomes again what it describes: patients, and under each the acts
   *  they underwent, in the order they happened.
   *
   *  The chosen sort still orders the GROUPS among themselves, on their most recent row. Inside a
   *  group the order is always chronological: a 2021 operation read after the 2026 revision would
   *  read as a revision of the revision. */
  const sortedExamples = useMemo(() => {
    const dossierOf = (e: DatasetExample) =>
      String(
        e.metadata?.patient_id_magex ??
          e.inputs?.patient_id_magex ??
          `${e.inputs?.tenant_id ?? ""}#${e.inputs?.id_patient ?? e.id}`,
      );
    const groups = new Map<string, DatasetExample[]>();
    for (const example of examples) {
      const key = dossierOf(example);
      groups.set(key, [...(groups.get(key) ?? []), example]);
    }
    const ranked = [...groups.values()].map((rows) => ({
      rows: [...rows].sort((a, b) => {
        // Undated first: it describes « tous les documents », hence the whole set of acts.
        if (!a.as_of || !b.as_of) return a.as_of ? 1 : b.as_of ? -1 : 0;
        return a.as_of.localeCompare(b.as_of);
      }),
      at: Math.max(...rows.map((r) => new Date(r.updated_at).getTime())),
    }));
    ranked.sort((a, b) => (sortDir === "desc" ? b.at - a.at : a.at - b.at));
    return ranked.flatMap(({ rows }) =>
      rows.map((example, rank) => ({
        example,
        /** The record's first row: it carries the name, the others hang off it. */
        opensDossier: rank === 0,
        /** How many acts this record contributes. 1 = the ordinary case, nothing to flag. */
        actsInDossier: rows.length,
      })),
    );
  }, [examples, sortDir]);

  if (examples.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <p><Trans>Aucun exemple dans ce jeu de données.</Trans></p>
        {!sealed && (
          <p className="text-sm mt-1">
            <Trans>Ajoutez des exemples manuellement ou importez un fichier CSV/JSON.</Trans>
          </p>
        )}
      </div>
    );
  }

  const colSpan = 8;

  const SortIcon = sortDir === "desc" ? ArrowDown : ArrowUp;

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            {!sealed && <TableHead className="w-16"><Trans>Entrées</Trans></TableHead>}
            <TableHead className="w-48"><Trans>Patient</Trans></TableHead>
            {!sealed && <TableHead className="w-52"><Trans>Revue</Trans></TableHead>}
            <TableHead className="w-32"><Trans>Date clinique</Trans></TableHead>
            {!sealed && <TableHead><Trans>Sorties attendues</Trans></TableHead>}
            <TableHead className="w-36">
              <button
                className="flex items-center gap-1 text-xs font-medium hover:text-primary transition-colors"
                onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
              >
                <Trans>Modifié le</Trans>
                {<SortIcon className="h-3 w-3" />}
              </button>
            </TableHead>
            {!sealed && <TableHead className="w-28"><Trans>Actions</Trans></TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedExamples.map(({ example, opensDossier, actsInDossier }, idx) => {
            const grouped = actsInDossier > 1;
            const patientLink = patientLinks.get(example.id);
            const patientName = patientNames.get(example.id);
            const fullName = patientName
              ? [patientName.prenom, patientName.nom].filter(Boolean).join(" ").trim()
              : "";
            // The patient page opens on the row's day: its cursor reads `?at=`.
            const datedLink =
              patientLink && example.as_of
                ? `${patientLink}?at=${example.as_of}`
                : patientLink;
            return (
              <Fragment key={example.id}>
                <TableRow
                  data-tour={idx === 0 ? "dataset-first-example" : undefined}
                  // The rule holds one record's acts together, and the top border separates it from
                  // the previous record: without it two neighbouring groups touch.
                  className={`${sealed ? "" : "cursor-pointer "}hover:bg-slate-50${
                    grouped ? " border-l-2 border-l-primary/30" : ""
                  }${grouped && opensDossier ? " border-t border-t-slate-200" : ""}`}
                  onClick={
                    sealed
                      ? undefined
                      : () => setExpandedId(expandedId === example.id ? null : example.id)
                  }
                >
                  <TableCell className="text-slate-500 font-mono text-sm">
                    {idx + 1}
                  </TableCell>
                  {!sealed && (
                  <TableCell>
                    {patientLink ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setModalTarget({
                            path: patientLink,
                            at: example.as_of ?? null,
                          });
                        }}
                        className="h-7 w-7 p-0 text-primary hover:bg-primary/10"
                        title={t`Voir le patient`}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    ) : patientLinksLoading ? (
                      <Loader2 className="h-3 w-3 text-slate-300 animate-spin" />
                    ) : (
                      <span className="text-slate-400 text-xs italic">—</span>
                    )}
                  </TableCell>
                  )}
                  <TableCell className="text-sm text-slate-700 truncate">
                    <div className="flex items-center gap-2">
                      {/* The name once per record. Repeating it on each act would read as as many
                          patients as there are operations; the chevron says this row is one more act
                          of the record opened above. */}
                      {grouped && !opensDossier && (
                        <span className="shrink-0 pl-2 text-slate-300" aria-hidden>
                          ↳
                        </span>
                      )}
                      <span className="truncate">
                        {grouped && !opensDossier ? (
                          <span className="text-xs text-slate-400">
                            <Plural
                              value={actsInDossier}
                              one="# acte de ce dossier"
                              other="# actes de ce dossier"
                            />
                          </span>
                        ) : fullName ? (
                          fullName
                        ) : patientLinksLoading ? (
                          <Loader2 className="h-3 w-3 text-slate-300 animate-spin" />
                        ) : (
                          <span className="text-slate-400 text-xs italic">—</span>
                        )}
                      </span>
                      {patientLink && (
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="h-7 w-7 p-0 text-primary hover:bg-primary/10 shrink-0"
                          title={t`Ouvrir la fiche complète`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link to={datedLink ?? patientLink} target="_blank">
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {!sealed && (
                  <TableCell className="text-sm">
                    {(() => {
                      const review = reviewByExampleId?.get(example.id);
                      if (!review) {
                        return (
                          <span className="text-slate-400 text-xs italic">
                            <Trans>Non revu</Trans>
                          </span>
                        );
                      }
                      // Le même découpage que l'onglet Corrections, et les mêmes
                      // couleurs que les pastilles de la fiche patient : vert validé,
                      // rouge corrigé, gris revu-sans-changement. Trois lectures d'un
                      // seul calcul, jamais trois calculs.
                      return (
                        <span className="flex flex-col gap-0.5 text-xs text-slate-700">
                          <span>
                            <Plural
                              value={review.reviewed}
                              one="# champ revu"
                              other="# champs revus"
                            />
                          </span>
                          <span className="flex flex-wrap gap-x-2 gap-y-0.5">
                            {review.validated > 0 && (
                              <span className="font-medium text-green-700">
                                <Plural
                                  value={review.validated}
                                  one="# validé"
                                  other="# validés"
                                />
                              </span>
                            )}
                            {review.corrected > 0 && (
                              <span className="font-medium text-red-600">
                                <Plural
                                  value={review.corrected}
                                  one="# corrigé"
                                  other="# corrigés"
                                />
                              </span>
                            )}
                            {review.flagged > 0 && (
                              <span className="font-medium text-slate-500">
                                <Trans>{review.flagged} sans changement</Trans>
                              </span>
                            )}
                          </span>
                        </span>
                      );
                    })()}
                  </TableCell>
                  )}
                  {/* Date clinique de la ligne : l'extraction n'y voit que les documents
                      datés ≤ cette date, et la note est calculée sur la valeur validée pour
                      ce jour-là. Un même patient peut donc apparaître sur plusieurs lignes.
                      « Tous les documents » = ligne non datée (état courant). */}
                  <TableCell className="whitespace-nowrap">
                    {example.as_of ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900">
                        <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {new Date(example.as_of).toLocaleDateString("fr-FR")}
                      </span>
                    ) : (
                      <span className="text-xs italic text-slate-400">
                        <Trans>Tous les documents</Trans>
                      </span>
                    )}
                    {/* The ACT this row speaks about, when it names one. The date alone does not
                        say it: on a revised record two rows carry two dates, and it is the
                        indication that explains why the answer key differs between them. */}
                    {(() => {
                      const act = episodeOf(example);
                      if (!act) return null;
                      return (
                        <span className="mt-1 block text-2xs text-slate-500">
                          <Trans>Ép. {act.rank}</Trans>
                          {act.label ? ` · ${act.label}` : ""}
                        </span>
                      );
                    })()}
                  </TableCell>
                  {/* The key, read as answers and not as truncated JSON. An object cut at 80
                      characters shows the ontology's first three fields and hides which ones — which
                      reads as "here is the key" when it is an arbitrary piece of it. */}
                  {!sealed && (
                  <TableCell className="text-xs max-w-xs">
                    {(() => {
                      const key = example.answer_key;
                      if (!key || key.size === 0) {
                        return <span className="text-slate-400 italic">—</span>;
                      }
                      const pairs = Object.entries(key.values);
                      return (
                        <div className="space-y-0.5">
                          {pairs.slice(0, 2).map(([field, value]) => (
                            <div key={field} className="truncate">
                              <span className="text-slate-500">{field}</span>
                              <span className="text-slate-400"> : </span>
                              <span className="text-slate-800">{readValue(value)}</span>
                            </div>
                          ))}
                          <div className="text-slate-400">
                            <Plural
                              value={key.size}
                              one="# réponse en tout"
                              other="# réponses en tout"
                            />
                            {key.emptied.length > 0 && (
                              <>
                                {" · "}
                                <Plural
                                  value={key.emptied.length}
                                  one="# validée vide"
                                  other="# validées vides"
                                />
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </TableCell>
                  )}
                  <TableCell className="text-xs text-slate-400 whitespace-nowrap">
                    {new Date(example.updated_at).toLocaleDateString("fr-FR", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </TableCell>
                  {!sealed && (
                  <TableCell>
                    <div className="flex gap-1">
                      {onEdit && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(example);
                          }}
                        >
                          {expandedId === example.id ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                      {onDelete && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(example.id);
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  )}
                </TableRow>
                {!sealed && expandedId === example.id && (
                  <TableRow key={`${example.id}-expanded`}>
                    <TableCell colSpan={colSpan} className="bg-slate-50 p-4">
                      <div>
                        <p className="text-sm font-semibold text-slate-700 mb-1">
                          <Trans>Sorties attendues</Trans>{" "}
                          <span className="font-normal text-slate-500">
                            <Trans>
                              — ce qu'une expérience note aujourd'hui ({example.answer_key?.size ?? 0}{" "}
                              champ(s))
                            </Trans>
                          </span>
                        </p>
                        {/* Field, value, and the DAY the answer describes.
                            A dated key shown without its days reads as a flat one: the value
                            validated for March is still the answer in September — nothing more
                            recent displaced it — and nothing on screen said which of the two dates
                            it described. It is what the patient page already writes in the label
                            (« au 15/06/2026 »). */}
                        {example.answer_key && example.answer_key.size > 0 ? (
                          <div className="bg-white border border-slate-200 rounded overflow-auto max-h-64">
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-slate-500 border-b border-slate-200">
                                  <th className="px-3 py-1.5 font-medium">
                                    <Trans>Champ</Trans>
                                  </th>
                                  <th className="px-3 py-1.5 font-medium">
                                    <Trans>Réponse validée</Trans>
                                  </th>
                                  <th className="px-3 py-1.5 font-medium whitespace-nowrap">
                                    <Trans>Jour décrit</Trans>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {Object.entries(example.answer_key.values).map(([field, value]) => {
                                  const day = shortDay(example.answer_key?.validated_on?.[field]);
                                  return (
                                    <tr key={field} className="border-b border-slate-100 last:border-0">
                                      <td className="px-3 py-1.5 text-slate-600">{field}</td>
                                      <td className="px-3 py-1.5 text-slate-900">{readValue(value)}</td>
                                      <td className="px-3 py-1.5 whitespace-nowrap text-slate-500 tabular-nums">
                                        {day ?? (
                                          <span className="italic text-slate-400">
                                            <Trans>état courant</Trans>
                                          </span>
                                        )}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 italic">
                            <Trans>Aucune réponse validée pour cette ligne.</Trans>
                          </p>
                        )}
                        {example.answer_key && example.answer_key.emptied.length > 0 && (
                          <p className="mt-1 text-xs text-slate-500">
                            <Trans>
                              Validés comme vides (répondre quelque chose y est une erreur) :
                            </Trans>{" "}
                            <span className="font-mono">{example.answer_key.emptied.join(", ")}</span>
                          </p>
                        )}
                      </div>
                      {example.expected_outputs && (
                        <details className="mt-3">
                          <summary className="cursor-pointer text-sm font-semibold text-slate-700">
                            <Trans>Instantané figé à l'association (expected_outputs)</Trans>
                          </summary>
                          <p className="mt-1 text-xs text-slate-500">
                            <Trans>
                              Conservé tel quel comme trace de ce qui était su à l'époque. Aucune
                              expérience ne note contre lui.
                            </Trans>
                          </p>
                          <pre className="mt-1 bg-white border border-slate-200 rounded p-3 text-xs overflow-auto max-h-48">
                            {JSON.stringify(example.expected_outputs, null, 2)}
                          </pre>
                        </details>
                      )}
                      {example.metadata && Object.keys(example.metadata).length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold text-slate-700 mb-1">
                            <Trans>Métadonnées</Trans>
                          </p>
                          {/* Read, not dumped. The act a dated row carries lives here, and hunting
                              for it in an indented JSON block amounts to not showing it. */}
                          <div className="bg-white border border-slate-200 rounded overflow-auto max-h-48">
                            <table className="w-full text-xs">
                              <tbody>
                                {Object.entries(example.metadata).map(([key, value]) => (
                                  <tr key={key} className="border-b border-slate-100 last:border-0">
                                    <td className="px-3 py-1.5 align-top text-slate-500 whitespace-nowrap">
                                      {METADATA_LABELS[key] ? t(METADATA_LABELS[key]) : key}
                                    </td>
                                    <td className="px-3 py-1.5 text-slate-900">
                                      {key === EPISODE_KEY ? describeEpisode(value, t) : readValue(value)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>
      {total != null && total > examples.length && onLoadMore && (
        <div className="mt-4 flex flex-col items-center gap-2 pb-4">
          <p className="text-sm text-slate-500">
            <Trans>{examples.length} sur {total} exemples chargés</Trans>
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onLoadMore}
            disabled={loadingMore}
            className="px-8"
          >
            {loadingMore
              ? t`Chargement...`
              : t`Charger ${Math.min(total - examples.length, 200)} exemples de plus`}
          </Button>
        </div>
      )}

      {modalTarget && (
        <PatientDetailModal
          open={true}
          onClose={() => setModalTarget(null)}
          patientPath={modalTarget.path}
          at={modalTarget.at}
        />
      )}
    </>
  );
}
